/**
 * Frequently Bought Together — checked-row totals and add-to-cart.
 *
 * Docs: docs/snippets/component-frequently-bought-together.md
 *
 * List layout: totals, SAVE, and the add-button label come from checked rows.
 * Bundle layout: required flavor rows, optional shared selling-plan frequency.
 * With a bundle product configured, the rows pick its variant and that one
 * variant is priced and added.
 *
 * Every line goes in one Cart.addMany call (one /cart/add.js request, one cart
 * read, one section render), as assets/section-complete-your-stack.js does.
 * Adding rows one Cart.add at a time cost a full add + cart read + drawer
 * render per product, which is what made a three-product bundle take 20-30 s.
 *
 * @extends HTMLElement
 */
export class FrequentlyBoughtTogether extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleMutation = this.handleMutation.bind(this);
    this.observer = null;
  }

  get isBundle() {
    return this.dataset.layout === 'bundle_configurator';
  }

  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleClick);

    const currentRow = this.querySelector('[id^="fbt-current-"]');
    if (currentRow) {
      this.observer = new MutationObserver(this.handleMutation);
      this.observer.observe(currentRow, {childList: true, subtree: true});
    }

    if (this.isBundle) {
      this.syncFrequencySelect();
    }

    this.updateTotals();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('click', this.handleClick);
    this.observer?.disconnect();
    this.observer = null;
  }

  /**
   * @param {Event} event
   */
  handleChange(event) {
    if (this.isBundle) {
      if (event.target.matches('[data-fbt-flavour]')) {
        this.applyFlavour(event.target);
        return;
      }

      if (event.target.matches('[data-fbt-purchase], [data-fbt-frequency]')) {
        this.updateTotals();
      }

      return;
    }

    if (!event.target.matches('.fbt__checkbox')) {
      return;
    }

    this.updateTotals();
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const button = event.target.closest('[data-fbt-add]');
    if (!button || !this.contains(button)) {
      return;
    }

    event.preventDefault();
    this.addChecked(button);
  }

  handleMutation() {
    this.updateTotals();
  }

  /**
   * @returns {{price: number, compareAt: number, variantId: string, sellingPlanId?: string}[]}
   */
  checkedRows() {
    if (this.isBundle) {
      return this.bundleLineItems();
    }

    const rows = [];

    for (const item of this.querySelectorAll('.fbt__item')) {
      const checkbox = item.querySelector('.fbt__checkbox');
      if (!checkbox?.checked) {
        continue;
      }

      const row = item.querySelector('[data-fbt-row]');
      if (!row?.dataset.variantId) {
        continue;
      }

      const price = Number(row.dataset.price) || 0;
      const compareAt = Number(row.dataset.compareAt) || price;
      rows.push({
        price,
        compareAt,
        variantId: row.dataset.variantId,
      });
    }

    return rows;
  }

  updateTotals() {
    if (this.isBundle) {
      this.updateBundleTotals();
      return;
    }

    const rows = this.checkedRows();
    let totalPrice = 0;
    let totalCompare = 0;

    for (const row of rows) {
      totalPrice += row.price;
      totalCompare += Math.max(row.compareAt, row.price);
    }

    const savings = totalCompare - totalPrice;
    const saveElement = this.querySelector('[data-fbt-save]');
    const totalElement = this.querySelector('[data-fbt-total]');
    const compareElement = this.querySelector('[data-fbt-compare-total]');
    const addButton = this.querySelector('[data-fbt-add]');
    const addLabel = addButton?.querySelector('[data-fbt-add-text]');

    if (totalElement) {
      totalElement.textContent = formatMoney(totalPrice, this.dataset.moneyFormat);
    }

    if (saveElement) {
      if (savings > 0) {
        saveElement.hidden = false;
        const template = this.dataset.saveLabel || '';
        saveElement.textContent = template.replace('[[amount]]', formatMoney(savings, this.dataset.moneyFormat));
      } else {
        saveElement.hidden = true;
      }
    }

    if (compareElement) {
      const compareAmount = compareElement.querySelector('[data-fbt-compare-amount]');
      if (savings > 0) {
        compareElement.hidden = false;
        if (compareAmount) {
          compareAmount.textContent = formatMoney(totalCompare, this.dataset.moneyFormat);
        }
      } else {
        compareElement.hidden = true;
      }
    }

    if (addButton) {
      addButton.disabled = (rows.length === 0);
    }

    if (addLabel) {
      const template = this.dataset.addLabel || '';
      addLabel.textContent = template.replace('[[count]]', String(rows.length));
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async addChecked(button) {
    if (!window.Cart) {
      const errorElement = this.querySelector('[data-fbt-error]');
      if (errorElement) {
        errorElement.textContent = this.dataset.errorLabel || '';
      }
      return;
    }

    const rows = this.checkedRows();
    if (!rows.length) {
      return;
    }

    const bundleVariant = this.hasBundleProduct ? this.selectedBundleVariant() : null;
    if (this.hasBundleProduct && !bundleVariant?.available) {
      return;
    }

    const items = bundleVariant
      ? [{id: Number(bundleVariant.id), quantity: 1}]
      : rows.map((row) => ({
          id: Number(row.variantId),
          quantity: 1,
          ...(row.sellingPlanId ? {selling_plan: Number(row.sellingPlanId)} : {}),
        }));

    const errorElement = this.querySelector('[data-fbt-error]');
    if (errorElement) {
      errorElement.textContent = '';
    }
    this.setBusy(button, true);

    try {
      await window.Cart.addMany(items, this);
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.description || error.message || this.dataset.errorLabel;
      }
    } finally {
      this.setBusy(button, false);
      this.updateTotals();
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} busy
   */
  setBusy(button, busy) {
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.querySelector('.fbt__spinner')?.classList.toggle('hidden', !busy);

    const label = button.querySelector('[data-fbt-add-text]');
    if (!label) {
      return;
    }

    if (busy) {
      label.dataset.idleText = label.textContent;
      label.textContent = button.dataset.addingLabel || label.textContent;
    } else if (label.dataset.idleText !== undefined) {
      label.textContent = label.dataset.idleText;
      delete label.dataset.idleText;
    }
  }

  get hasBundleProduct() {
    return Boolean(this.querySelector('[data-fbt-bundle-variants]'));
  }

  /**
   * @returns {{id: number, options: string[], price: number, available: boolean}[]}
   */
  get bundleVariants() {
    if (this._bundleVariants) {
      return this._bundleVariants;
    }

    try {
      this._bundleVariants = JSON.parse(this.querySelector('[data-fbt-bundle-variants]')?.textContent || '[]');
    } catch {
      this._bundleVariants = [];
    }

    return this._bundleVariants;
  }

  /**
   * The bundle variant whose options match the flavours picked in the rows.
   * A row names its bundle option by position (data-bundle-option, 1-based).
   * A bundle option with more than one value and no row mapped to it leaves
   * the selection unresolved (null), so the shopper is never charged and
   * shipped a flavour they did not pick — that happens when a product title
   * and the bundle's option name drift apart.
   * @returns {{id: number, options: string[], price: number, available: boolean} | null}
   */
  selectedBundleVariant() {
    const wanted = new Map();
    const optionCount = this.bundleVariants[0]?.options.length || 0;

    for (const row of this.bundleRows()) {
      const position = Number(row.dataset.bundleOption);
      if (!position) {
        continue;
      }

      const {entry} = this.getRowState(row);
      if (entry.flavour) {
        wanted.set(position - 1, normalizeOptionValue(entry.flavour));
      }
    }

    for (let index = 0; index < optionCount; index++) {
      const values = new Set(this.bundleVariants.map((variant) => normalizeOptionValue(variant.options[index])));
      if (!wanted.has(index) && values.size > 1) {
        return null;
      }
    }

    const matches = this.bundleVariants.filter((variant) =>
      [...wanted].every(([index, value]) => normalizeOptionValue(variant.options[index]) === value),
    );

    return matches.find((variant) => variant.available) || matches[0] || null;
  }

  /**
   * @param {HTMLInputElement} input
   */
  applyFlavour(input) {
    const row = input.closest('[data-fbt-row]');
    if (!row) {
      return;
    }

    row.dataset.variantId = input.value;
    const entry = this.getVariantMap(row)[input.value];
    if (!entry) {
      this.syncFrequencySelect();
      this.updateTotals();
      return;
    }

    row.dataset.price = String(entry.otp || 0);

    const img = row.querySelector('.fbt-bundle__img');
    if (img && entry.image && img.tagName === 'IMG') {
      img.src = entry.image;
    }

    const flavourLabel = row.querySelector('[data-fbt-flavour-label]');
    if (flavourLabel && entry.flavour) {
      flavourLabel.textContent = entry.flavour;
    }

    this.syncFrequencySelect();
    this.updateTotals();
  }

  /**
   * @param {HTMLElement} row
   * @returns {Record<string, {otp?: number, compare?: number, image?: string, flavour?: string, plans?: {id: number, label: string, price: number}[]}>}
   */
  getVariantMap(row) {
    if (row._variantMap) {
      return row._variantMap;
    }

    const script = row.querySelector('[data-fbt-variant-map]');
    try {
      row._variantMap = JSON.parse(script?.textContent || '{}');
    } catch {
      row._variantMap = {};
    }

    return row._variantMap;
  }

  /**
   * @returns {HTMLElement[]}
   */
  bundleRows() {
    return Array.from(this.querySelectorAll('[data-fbt-row]'));
  }

  /**
   * @param {HTMLElement} row
   */
  getRowState(row) {
    const map = this.getVariantMap(row);
    const variantId = row.dataset.variantId;
    const entry = map[variantId] || {};
    const otp = Number(entry.otp) || Number(row.dataset.price) || 0;
    const plans = Array.isArray(entry.plans) ? entry.plans : [];
    const frequency = this.selectedFrequencyLabel();
    const plan = plans.find((item) => normalizeFrequencyLabel(item.label) === frequency);

    return {variantId, otp, plans, plan, entry};
  }

  selectedFrequencyLabel() {
    return this.querySelector('[data-fbt-frequency]')?.value || '';
  }

  isSubscribeSelected() {
    if (this.dataset.enableSubscription !== 'true') {
      return false;
    }

    const radio = this.querySelector('[data-fbt-purchase]:checked');
    return radio?.value === 'subscribe';
  }

  /**
   * @returns {string[]}
   */
  sharedFrequencyLabels() {
    let shared = null;

    for (const row of this.bundleRows()) {
      const {plans} = this.getRowState(row);
      const labels = new Set(plans.map((plan) => normalizeFrequencyLabel(plan.label)).filter(Boolean));

      if (shared === null) {
        shared = labels;
      } else {
        shared = new Set([...shared].filter((label) => labels.has(label)));
      }
    }

    return shared ? [...shared] : [];
  }

  syncFrequencySelect() {
    const select = this.querySelector('[data-fbt-frequency]');
    if (!select) {
      return;
    }

    const current = select.value;
    const labels = this.sharedFrequencyLabels();
    const template = this.dataset.frequencyEvery || 'Deliver every [[interval]]';

    select.replaceChildren();

    for (const label of labels) {
      const option = document.createElement('option');
      option.value = label;
      option.textContent = template.replace('[[interval]]', label);
      select.appendChild(option);
    }

    if (labels.includes(current)) {
      select.value = current;
    }

    const canSubscribe = labels.length > 0 && this.dataset.enableSubscription === 'true';
    const purchaseWrap = this.querySelector('[data-fbt-purchase-wrap]');
    if (purchaseWrap) {
      purchaseWrap.hidden = !canSubscribe;
    }

    if (!canSubscribe) {
      const otp = this.querySelector('[data-fbt-purchase][value="otp"]');
      if (otp) {
        otp.checked = true;
      }
    }
  }

  /**
   * @returns {{price: number, compareAt: number, variantId: string, sellingPlanId?: string}[]}
   */
  bundleLineItems() {
    const subscribe = this.isSubscribeSelected() && this.sharedFrequencyLabels().length > 0;

    return this.bundleRows()
      .map((row) => {
        const state = this.getRowState(row);
        if (!state.variantId) {
          return null;
        }

        const sellingPlanId = subscribe && state.plan?.id ? String(state.plan.id) : '';
        const price = subscribe && state.plan ? Number(state.plan.price) || state.otp : state.otp;

        return {
          variantId: state.variantId,
          price,
          compareAt: Number(state.entry.compare) || state.otp,
          sellingPlanId,
        };
      })
      .filter(Boolean);
  }

  updateBundleTotals() {
    const labels = this.sharedFrequencyLabels();
    const subscribe = this.isSubscribeSelected() && labels.length > 0;
    let otpTotal = 0;
    let subTotal = 0;

    for (const row of this.bundleRows()) {
      const state = this.getRowState(row);
      otpTotal += state.otp;
      subTotal += subscribe && state.plan ? Number(state.plan.price) || state.otp : state.otp;
    }

    const bundleVariant = this.hasBundleProduct ? this.selectedBundleVariant() : null;
    const display = bundleVariant ? bundleVariant.price : subscribe ? subTotal : otpTotal;
    const totalElement = this.querySelector('[data-fbt-total]');
    if (totalElement) {
      totalElement.textContent = formatMoney(display, this.dataset.moneyFormat);
    }

    const compareElement = this.querySelector('[data-fbt-compare-total]');
    if (compareElement) {
      compareElement.hidden = !(bundleVariant && otpTotal > display);
      const compareAmount = compareElement.querySelector('[data-fbt-compare-amount]');
      if (compareAmount) {
        compareAmount.textContent = formatMoney(otpTotal, this.dataset.moneyFormat);
      }
    }

    const savings = otpTotal - subTotal;
    const saveElement = this.querySelector('[data-fbt-subscribe-save]');
    if (saveElement) {
      if (subscribe && savings > 0) {
        saveElement.hidden = false;
        saveElement.textContent = decodeHtmlEntities(this.dataset.saveSubscribeLabel || '').replace(
          '[[amount]]',
          formatMoney(savings, this.dataset.moneyFormat),
        );
      } else {
        saveElement.hidden = true;
      }
    }

    const percent = otpTotal > 0 && savings > 0 ? Math.round((savings * 100) / otpTotal) : 0;
    const percentEl = this.querySelector('[data-fbt-save-pct]');
    if (percentEl) {
      percentEl.textContent = `${percent}%`;
    }

    const addButton = this.querySelector('[data-fbt-add]');
    const addLabel = addButton?.querySelector('[data-fbt-add-text]');
    const soldOut = this.hasBundleProduct && !bundleVariant?.available;
    if (addButton && addButton.getAttribute('aria-busy') !== 'true') {
      addButton.disabled = this.bundleRows().length === 0 || soldOut;
    }
    if (addLabel && addButton?.getAttribute('aria-busy') !== 'true') {
      let label = subscribe ? this.dataset.subscribeLabel : this.dataset.buyLabel;
      if (soldOut) {
        label = bundleVariant ? this.dataset.soldOutLabel : this.dataset.unavailableLabel;
      }
      addLabel.textContent = decodeHtmlEntities(label || '');
    }

    this.querySelectorAll('.fbt-bundle__toggle-option').forEach((option) => {
      const radio = option.querySelector('[data-fbt-purchase]');
      option.classList.toggle('is-active', Boolean(radio?.checked));
    });

    const frequencyWrap = this.querySelector('[data-fbt-frequency-wrap]');
    if (frequencyWrap) {
      frequencyWrap.hidden = !subscribe;
    }
  }
}

/**
 * @param {string} [label]
 * @returns {string}
 */
/**
 * @param {string} [value]
 * @returns {string}
 */
function decodeHtmlEntities(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/**
 * @param {string} [value]
 * @returns {string}
 */
function normalizeOptionValue(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeFrequencyLabel(label) {
  if (!label) {
    return '';
  }

  return String(label)
    .replace(/^deliver(?:y)?\s+every\s+/i, '')
    .replace(/^every\s+/i, '')
    .trim();
}

/**
 * @param {number} cents
 * @param {string} [format]
 * @returns {string}
 */
function formatMoney(cents, format) {
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const amountPlaceholder = '{{amount}}';
  const formatString = format || `$${amountPlaceholder}`;

  /**
   * @param {number} number
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   * @returns {string}
   */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (Number.isNaN(number) || number == null) {
      return '0';
    }

    const parts = (number / 100.0).toFixed(precision).split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? dollars + decimal + parts[1] : dollars;
  }

  const match = formatString.match(placeholder);
  const token = match ? match[1] : 'amount';
  let value = '';

  switch (token) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2, ',', '.');
  }

  return formatString.replace(placeholder, value);
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
